import { useQuery } from "@tanstack/react-query";
import { Chip } from "@mui/material";
import { diagnosticsApi, type DxLabTestDto } from "@/api/diagnostics";
import { servicesApi } from "@/api/endpoints";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SelectCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/tests";
const LIST_KEY = ["dx-tests"];

export function DiagnosticsLabTestsPage() {
  const departments = useQuery({ queryKey: ["dx-departments"], queryFn: () => diagnosticsApi.listDepartments() });
  const deptName = new Map((departments.data ?? []).map(d => [d.id, `${d.code} — ${d.name}`]));

  return (
    <MasterListPage<DxLabTestDto>
      breadcrumbs={["Diagnostics", "Masters"]}
      title="Lab tests"
      subtitle="The lab catalog. Price and GST come from the linked billing service."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listTests(undefined, inc)}
      deleteFn={diagnosticsApi.deleteTest}
      basePath={BASE}
      newLabel="New lab test"
      labelOf={(r) => r.name}
      deleteNote="It will be refused if it belongs to a profile or reflex rule, or already has results or orders."
      columns={[
        { field: "code", headerName: "Code", width: 130 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
        {
          field: "departmentId", headerName: "Department", width: 190,
          renderCell: (p) => deptName.get(p.row.departmentId) ?? "—",
        },
        {
          field: "unit", headerName: "Unit", width: 100,
          valueFormatter: (v: string | null | undefined) => v ?? "—",
        },
        {
          field: "isOutsourced", headerName: "Outsourced", width: 120,
          renderCell: (p) => p.row.isOutsourced
            ? <Chip size="small" label="Yes" color="warning" variant="outlined" />
            : <>—</>,
        },
        statusColumn<DxLabTestDto>(),
      ]}
    />
  );
}

interface Values {
  departmentId: string; categoryId: string; code: string; name: string; unit: string;
  serviceId: string; defaultSpecimenId: string; defaultContainerId: string; reportTemplateId: string;
  criticalLow: string; criticalHigh: string; estimatedMinutes: string; tatTargetMinutes: string;
  subCategory: string; loincCode: string; cptCode: string; methodology: string; analyzerMapping: string;
  deltaCheckLimit: string; isOutsourced: boolean; isActive: boolean;
}

const num = (s: string) => (s === "" ? null : Number(s));
const str = (s: string) => s.trim() || null;

/**
 * Lab-test create/edit. Reference ranges are intentionally not edited here — they carry their own
 * domain invariants and the API leaves them untouched on update.
 */
export function LabTestFormPage() {
  const departments = useQuery({ queryKey: ["dx-departments"], queryFn: () => diagnosticsApi.listDepartments() });
  const categories = useQuery({ queryKey: ["dx-categories"], queryFn: () => diagnosticsApi.listCategories() });
  const specimens = useQuery({ queryKey: ["dx-specimens"], queryFn: () => diagnosticsApi.listSpecimens() });
  const containers = useQuery({ queryKey: ["dx-containers"], queryFn: () => diagnosticsApi.listContainers() });
  const templates = useQuery({ queryKey: ["dx-report-templates"], queryFn: () => diagnosticsApi.listReportTemplates() });
  const services = useQuery({ queryKey: ["services-all"], queryFn: () => servicesApi.list({ pageSize: 200 }) });

  const opts = <T extends { id: string; code: string; name: string }>(rows?: T[]) =>
    (rows ?? []).map(r => ({ value: r.id, label: `${r.code} — ${r.name}` }));

  return (
    <MasterFormPage<Values, DxLabTestDto>
      breadcrumbs={["Diagnostics", "Lab tests"]}
      noun="lab test"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-test", id]}
      maxWidth={960}
      defaultValues={{
        departmentId: "", categoryId: "", code: "", name: "", unit: "",
        serviceId: "", defaultSpecimenId: "", defaultContainerId: "", reportTemplateId: "",
        criticalLow: "", criticalHigh: "", estimatedMinutes: "", tatTargetMinutes: "",
        subCategory: "", loincCode: "", cptCode: "", methodology: "", analyzerMapping: "",
        deltaCheckLimit: "", isOutsourced: false, isActive: true,
      }}
      getFn={diagnosticsApi.getTest}
      toValues={(d) => ({
        departmentId: d.departmentId, categoryId: d.categoryId ?? "", code: d.code, name: d.name,
        unit: d.unit ?? "", serviceId: d.serviceId ?? "",
        defaultSpecimenId: d.defaultSpecimenId ?? "", defaultContainerId: d.defaultContainerId ?? "",
        reportTemplateId: d.reportTemplateId ?? "",
        criticalLow: d.criticalLow == null ? "" : String(d.criticalLow),
        criticalHigh: d.criticalHigh == null ? "" : String(d.criticalHigh),
        estimatedMinutes: d.estimatedMinutes == null ? "" : String(d.estimatedMinutes),
        tatTargetMinutes: d.tatTargetMinutes == null ? "" : String(d.tatTargetMinutes),
        subCategory: d.subCategory ?? "", loincCode: d.loincCode ?? "", cptCode: d.cptCode ?? "",
        methodology: d.methodology ?? "", analyzerMapping: d.analyzerMapping ?? "",
        deltaCheckLimit: d.deltaCheckLimit == null ? "" : String(d.deltaCheckLimit),
        isOutsourced: d.isOutsourced, isActive: d.isActive,
      })}
      createFn={(v) => diagnosticsApi.createTest({
        departmentId: v.departmentId, categoryId: v.categoryId || null,
        code: v.code.trim(), name: v.name.trim(), unit: str(v.unit),
        criticalLow: num(v.criticalLow), criticalHigh: num(v.criticalHigh), serviceId: v.serviceId || null,
        defaultSpecimenId: v.defaultSpecimenId || null, defaultContainerId: v.defaultContainerId || null,
        estimatedMinutes: num(v.estimatedMinutes), subCategory: str(v.subCategory),
        loincCode: str(v.loincCode), cptCode: str(v.cptCode), methodology: str(v.methodology),
        analyzerMapping: str(v.analyzerMapping), tatTargetMinutes: num(v.tatTargetMinutes),
        reportTemplateId: v.reportTemplateId || null, isOutsourced: v.isOutsourced,
        deltaCheckLimit: num(v.deltaCheckLimit),
      })}
      updateFn={(id, v) => diagnosticsApi.updateTest(id, {
        departmentId: v.departmentId, categoryId: v.categoryId || null,
        code: v.code.trim(), name: v.name.trim(), unit: str(v.unit),
        criticalLow: num(v.criticalLow), criticalHigh: num(v.criticalHigh), serviceId: v.serviceId || null,
        defaultSpecimenId: v.defaultSpecimenId || null, defaultContainerId: v.defaultContainerId || null,
        estimatedMinutes: num(v.estimatedMinutes), subCategory: str(v.subCategory),
        loincCode: str(v.loincCode), cptCode: str(v.cptCode), methodology: str(v.methodology),
        analyzerMapping: str(v.analyzerMapping), tatTargetMinutes: num(v.tatTargetMinutes),
        reportTemplateId: v.reportTemplateId || null, isOutsourced: v.isOutsourced,
        deltaCheckLimit: num(v.deltaCheckLimit), isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <SelectCell control={control} name="departmentId" label="Department" span={6} required
            options={opts(departments.data)} emptyLabel="— select department —" />
          <SelectCell control={control} name="categoryId" label="Category" span={6}
            options={opts(categories.data)} />
          <TextFieldCell control={control} name="code" label="Code" span={3} required />
          <TextFieldCell control={control} name="name" label="Name" span={6} required />
          <TextFieldCell control={control} name="unit" label="Unit" span={3} />

          <SelectCell control={control} name="serviceId" label="Billing service (price/GST)" span={12}
            options={(services.data?.items ?? []).map(s => ({ value: s.id, label: `${s.code} — ${s.name}` }))} />

          <SelectCell control={control} name="defaultSpecimenId" label="Default specimen" span={4}
            options={opts(specimens.data)} />
          <SelectCell control={control} name="defaultContainerId" label="Default container" span={4}
            options={opts(containers.data)} />
          <SelectCell control={control} name="reportTemplateId" label="Report template" span={4}
            options={opts(templates.data)} />

          <TextFieldCell control={control} name="criticalLow" label="Critical low" span={3} type="number" />
          <TextFieldCell control={control} name="criticalHigh" label="Critical high" span={3} type="number" />
          <TextFieldCell control={control} name="deltaCheckLimit" label="Delta-check limit" span={3} type="number" />
          <TextFieldCell control={control} name="tatTargetMinutes" label="TAT target (mins)" span={3} type="number" />

          <TextFieldCell control={control} name="subCategory" label="Sub-category" span={4} />
          <TextFieldCell control={control} name="loincCode" label="LOINC code" span={4} />
          <TextFieldCell control={control} name="cptCode" label="CPT code" span={4} />
          <TextFieldCell control={control} name="methodology" label="Methodology" span={6} />
          <TextFieldCell control={control} name="analyzerMapping" label="Analyzer mapping" span={6} />
          <TextFieldCell control={control} name="estimatedMinutes" label="Est. processing minutes" span={4} type="number" />

          <SwitchCell control={control} name="isOutsourced" label="Sent to an external lab" />
          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
